import { CognitoIdToken } from 'amazon-cognito-identity-js';
import { httpCreators, HttpHelper, HttpResponse } from '@ppvrbh/merchant-frontend-core';
import { call, put, takeLatest } from 'redux-saga/effects';
import { mainCreators } from '../main/main-action';
import { mainSagas } from '../main/main-saga';
import { generalCreators } from '../redux/general/general-action';
import {
  GENERAL_REQUEST_ID_LIST,
  MODAL_TYPE,
} from '../redux/general/general-constant';
import { GENERAL_STATE_KEY } from '../redux/general/general-reducer';
import { GeneralService } from '../redux/general/general-service';
import { registerLocalStorage } from '../shared/helpers/utility';
import { authCreators } from './auth-action';
import {
  ACCESS_TOKEN_HEADER,
  AUTH_LOCAL_STORAGE_KEY,
  AUTH_REQUEST_ID_LIST,
  AUTH_USER_ROLE,
  COGNITO_CHALLENGE,
  COGNITO_LOCAL_STORAGE_KEY,
  COGNITO_RESPONSE,
} from './auth-constant';
import { AuthHelper } from './auth-helper';
import { AUTH_STATE_KEY } from './auth-reducer';
import { AuthService } from './auth-services';
import { AUTH_ACTION_TYPE, AuthToken, SignInRespData, TokenPayload } from './auth-type';
import { noop } from 'lodash';
import { jwtDecode } from 'jwt-decode';

function* loginHandler(resp: HttpResponse<SignInRespData>) {
  if (resp.success) {
    if (resp.data.token) {
      const tokens = resp.data.token;
      yield call(applyToken, tokens);
      yield call(AuthHelper.setTokenExpireTime, tokens.idToken);
      yield put(
        authCreators.setAuthState(
          AUTH_STATE_KEY.STATUS,
          COGNITO_CHALLENGE.LOGIN_SUCCESS
        )
      );
    } else if (resp.data.challenge) {
      //Implementation for challenge
      const challenge = resp.data.challenge;
      if (challenge.name === COGNITO_CHALLENGE.NEW_PASSWORD_REQUIRED) {
        yield put(
          authCreators.setAuthState(AUTH_STATE_KEY.SESSION, challenge.session)
        );
        yield put(
          authCreators.setAuthState(
            AUTH_STATE_KEY.STATUS,
            COGNITO_CHALLENGE.NEW_PASSWORD_REQUIRED
          )
        );
      }
    }
  } else if (resp.statusCode != 200) {
    yield put(
      generalCreators.setGeneralState(GENERAL_STATE_KEY.MODAL, {
        visible: true,
        type: MODAL_TYPE.ERROR,
        modalTitle: 'main.login.error.model.title',
        modalContent: 'main.login.error.model.content',
      })
    );
  } else {
    yield put(
      generalCreators.setGeneralState(GENERAL_STATE_KEY.MODAL, {
        visible: true,
        onOk: noop,
        type: MODAL_TYPE.ERROR,
      })
    );
  }
}

function* currentSession() {
  const resp = yield call(AuthHelper.getCurrentSession);
  if (resp.data) {
    yield call(currentSessionHandle, resp.data);
  }
}

function* currentSessionHandle(tokens: AuthToken) {
  if (tokens) {
    yield call(authSagas.applyToken, tokens);
    yield call(AuthHelper.setTokenExpireTime, tokens.idToken);
    yield put(
      authCreators.setAuthState(
        AUTH_STATE_KEY.STATUS,
        COGNITO_CHALLENGE.LOGIN_SUCCESS
      )
    );
  } else {
    yield call(authSagas.logout);
  }
}

function* applyToken(tokens: AuthToken) {
  const idToken = tokens.idToken;
  const role: AUTH_USER_ROLE | undefined = yield call(AuthHelper.getRole, tokens);
  yield call(HttpHelper.setHeader, ACCESS_TOKEN_HEADER, idToken);
  yield put(authCreators.setAuthState(AUTH_STATE_KEY.ID_TOKEN, 'login'));
  yield put(authCreators.setAuthState(AUTH_STATE_KEY.ROLE, role));
  yield put(authCreators.setAuthState(AUTH_STATE_KEY.EMAIL, jwtDecode<TokenPayload>(idToken)?.email));
  
  yield call(registerLocalStorage, AUTH_LOCAL_STORAGE_KEY.ID_TOKEN, idToken);
  if (tokens.refreshToken) {
    yield call(registerLocalStorage, AUTH_LOCAL_STORAGE_KEY.REFRESH_TOKEN, tokens.refreshToken);
  }
  
  const merchantTypeId: number = yield call(AuthHelper.getMerchantTypeId);
  const businessTypeId: number = yield call(AuthHelper.getBusinessTypeId);
  if (merchantTypeId && businessTypeId) {
    yield put(mainCreators.callMasterDataAndLandingStatus());
  } else {
    yield call(mainSagas.preload);
  }
}

function* postValidate(response: HttpResponse, callbackParams: any) {
  if (response.success) {
    if (response.data.validate) {
      const email = callbackParams.data;
      const merchantUserId = String(response.data.merchantUserId);
      yield put(
        httpCreators.request({
          service: AuthService.checkMailIsFirstLogin,
          serviceParams: email,
          id: AUTH_REQUEST_ID_LIST.IS_FIRST_LOGIN,
          sagaHandler: authSagas.forgotPassword,
          callbackParams: {
            merchantUserId,
            requestForgotPassword: callbackParams.requestForgotPassword,
            resendSuccess: callbackParams.resendSuccess,
          },
        })
      );
    } else {
      yield put(
        generalCreators.setGeneralState(GENERAL_STATE_KEY.MODAL, {
          visible: true,
          onOk: noop,
          type: MODAL_TYPE.ERROR,
          modalTitle: 'main.forgotpassword.verifyemail.modal.title',
          modalContent: 'main.forgotpassword.verifyemail.modal.description',
        })
      );
    }
  } else {
    yield put(
      generalCreators.setGeneralState(GENERAL_STATE_KEY.MODAL, {
        visible: true,
        onOk: noop,
        type: MODAL_TYPE.ERROR,
      })
    );
  }
}

function* forgotPassword(response: HttpResponse, callbackParams: any) {
  if (response.success) {
    if (response.data?.message === COGNITO_RESPONSE.FORCE_CHANGE_PASSWORD) {
      callbackParams.resendSuccess();
    } else if (response.data?.message === COGNITO_RESPONSE.FORGOT_PASSWORD) {
      callbackParams.requestForgotPassword(callbackParams.merchantUserId);
    }
  } else {
    yield put(
      generalCreators.setGeneralState(GENERAL_STATE_KEY.MODAL, {
        visible: true,
        onOk: noop,
        type: MODAL_TYPE.ERROR,
      })
    );
  }
}

function* forgotPasswordHandle(response: any, callbackParams: any) {
  if (response.success) {
    registerLocalStorage(
      COGNITO_LOCAL_STORAGE_KEY.MERCHANT_USER_ID,
      response.merchantUserId
    );
    yield put(
      generalCreators.setGeneralState(GENERAL_STATE_KEY.MODAL, {
        visible: true,
        onOk: callbackParams,
        type: MODAL_TYPE.ERROR,
        modalTitle: 'main.forgotpassword.successbox.title',
        modalContent: 'main.forgotpassword.successbox.description',
      })
    );
  } else if (response?.code === COGNITO_RESPONSE.LIMIT_EXCEEDED_EXCEPTION) {
    yield put(
      generalCreators.setGeneralState(GENERAL_STATE_KEY.MODAL, {
        visible: true,
        onOk: noop,
        type: MODAL_TYPE.ERROR,
        modalTitle: 'main.forgotpassword.sendemail.modal.title',
        modalContent: 'main.forgotpassword.sendemail.modal.description',
      })
    );
  } else {
    yield put(
      generalCreators.setGeneralState(GENERAL_STATE_KEY.MODAL, {
        visible: true,
        onOk: noop,
        type: MODAL_TYPE.ERROR,
      })
    );
  }
}

function* forgotPasswordConfirmHandle(response: any, callbackParams: any) {
  if (response.success) {
    callbackParams();
  } else {
    switch (response.code) {
      case COGNITO_RESPONSE.LIMIT_EXCEEDED_EXCEPTION:
        yield put(
          generalCreators.setGeneralState(GENERAL_STATE_KEY.MODAL, {
            visible: true,
            onOk: noop,
            type: MODAL_TYPE.ERROR,
            modalTitle:
              'main.setnewpassword.error.limitExceededException.title',
            modalContent:
              'main.setnewpassword.error.limitExceededException.description',
          })
        );
        break;

      case COGNITO_RESPONSE.CODE_MISMATCH_EXCEPTION:
      case COGNITO_RESPONSE.INVALID_PARAMETER_EXCEPTION:
        yield put(
          generalCreators.setGeneralState(GENERAL_STATE_KEY.MODAL, {
            visible: true,
            onOk: noop,
            type: MODAL_TYPE.ERROR,
            modalTitle: 'main.setnewpassword.error.codeMismatchException.title',
            modalContent:
              'main.setnewpassword.error.codeMismatchException.description',
          })
        );
        break;

      case COGNITO_RESPONSE.EXPIRED_CODE_EXCEPTION:
        yield put(
          generalCreators.setGeneralState(GENERAL_STATE_KEY.MODAL, {
            visible: true,
            onOk: noop,
            type: MODAL_TYPE.ERROR,
            modalTitle: 'main.setnewpassword.error.expiredCodeException.title',
            modalContent:
              'main.setnewpassword.error.expiredCodeException.description',
          })
        );
        break;

      default:
        yield put(
          httpCreators.request({
            service: GeneralService.postWebLog,
            serviceParams: response,
            id: GENERAL_REQUEST_ID_LIST.POST_WEB_LOG,
          })
        );
        yield put(
          generalCreators.setGeneralState(GENERAL_STATE_KEY.MODAL, {
            visible: true,
            onOk: noop,
            type: MODAL_TYPE.ERROR,
          })
        );
        break;
    }
  }
}

function* confirmNewPasswordHandle(response: any, callbackParams: any) {
  if (response.success) {
    callbackParams();
  } else {
    yield put(
      httpCreators.request({
        service: GeneralService.postWebLog,
        serviceParams: response,
        id: GENERAL_REQUEST_ID_LIST.POST_WEB_LOG,
      })
    );
    yield put(
      generalCreators.setGeneralState(GENERAL_STATE_KEY.MODAL, {
        visible: true,
        onOk: noop,
        type: MODAL_TYPE.ERROR,
      })
    );
  }
}

export function* logout() {
  yield call([AuthHelper, 'logOut']);
}

export function checkAuthorization() {}

export const authSagas = {
  logout,
  applyToken,
  currentSession,
  postValidate,
  forgotPassword,
  forgotPasswordHandle,
  confirmNewPasswordHandle,
  forgotPasswordConfirmHandle,
  currentSessionHandle,
  loginHandler,
  watchers: [
    takeLatest(AUTH_ACTION_TYPE.LOGOUT, logout),
    takeLatest(AUTH_ACTION_TYPE.CHECK_AUTHORIZATION, checkAuthorization),
  ],
};
